import { useCallback, useEffect, useRef, useState } from 'react';
import { GetEmail, GetMonitoringCurrent, GetSetup } from '../../Api/getData';
import { setupMonitoringVessels, vesselKey } from './monitoringData';
import { mergeCurrentRows, readCurrentCache, saveCurrentCache } from './currentCache';

async function withWorkers(items, worker) {
    let index = 0;
    await Promise.all([0, 1].map(async () => {
        while (index < items.length) await worker(items[index++]);
    }));
}
const sessionError = error => [401, 403].includes(error.response?.status);
const readingFailure = (vessel, error) => ({
    currentState: sessionError(error) ? 'auth-error' : vessel.current?.length ? 'stale' : 'retrying',
    currentRetry: !sessionError(error),
});

export default function useMonitoringData() {
    const [data, setData] = useState([]);
    const [setupLoading, setSetupLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [refreshLoading, setRefreshLoading] = useState(true);
    const activeRequest = useRef(null);
    const refreshQueued = useRef(false);
    const retryRequest = useRef(null);
    const latestData = useRef(data);
    latestData.current = data;

    const refresh = useCallback(async function fetchData(options = {}) {
        if (activeRequest.current) {
            if (options.queue) refreshQueued.current = true;
            return;
        }
        const controller = new AbortController();
        activeRequest.current = controller;
        const isActive = () => activeRequest.current === controller && !controller.signal.aborted;
        const updateVessel = (key, patch) => {
            if (isActive()) setData(previous => previous.map(vessel => vesselKey(vessel) === key
                ? { ...vessel, ...(typeof patch === 'function' ? patch(vessel) : patch) } : vessel));
        };

        setSetupLoading(true);
        setRefreshLoading(true);
        setLoadError('');
        try {
            const setup = await GetSetup(undefined, { signal: controller.signal, timeout: 60000 });
            if (!isActive()) return;
            if (!Array.isArray(setup)) throw new Error('Invalid vessel setup response');
            const vessels = setupMonitoringVessels(setup);
            // Publish the directory before starting either sensor or inbox requests.
            // Keep existing positions visible during a refresh, with their report dates.
            setData(previous => {
                const cached = readCurrentCache();
                const previousByKey = new Map(previous.map(vessel => [vesselKey(vessel), vessel]));
                return vessels.map(vessel => {
                    const previousVessel = previousByKey.get(vesselKey(vessel));
                    const current = previousVessel?.current?.length ? previousVessel.current : cached[vesselKey(vessel)] || [];
                    return { ...vessel, current, currentState: current.length ? 'refreshing' : 'loading', currentRetry: false,
                        email: previousVessel?.email || null };
                });
            });
            setSetupLoading(false);

            let currentFailures = 0;
            let emailFailures = 0;
            const loadPositions = async () => {
                await Promise.all(vessels.map(async vessel => {
                    try {
                        const email = await GetEmail({ ObjectCode: vessel.ShipCode }, controller.signal, { timeout: 120000 });
                        updateVessel(vesselKey(vessel), { email, emailState: 'ready' });
                    } catch {
                        if (!isActive()) return;
                        emailFailures += 1;
                        updateVessel(vesselKey(vessel), { emailState: 'error' });
                    }
                }));
            };
            const loadReadings = async () => {
                // Monitoring only reads committed calibrated history.
                await withWorkers(vessels, async vessel => {
                    if (!isActive()) return;
                    try {
                        const current = await GetMonitoringCurrent({ AccountId: vessel.KodeCabang, ObjectCode: vessel.ShipCode }, {
                            signal: controller.signal, timeout: 130000,
                        });
                        if (!isActive()) return;
                        if (!Array.isArray(current)) throw new Error('Invalid saved sensor response');
                        saveCurrentCache(vesselKey(vessel), current);
                        updateVessel(vesselKey(vessel), existing => ({ current: mergeCurrentRows(current, existing.current), currentState: current.length ? 'saved' : 'empty', currentRetry: false }));
                    } catch (error) {
                        if (!isActive()) return;
                        currentFailures += 1;
                        updateVessel(vesselKey(vessel), existing => readingFailure(existing, error));
                    }
                });
            };
            // Neither resource blocks vessel identities or the other resource.
            await Promise.all([loadPositions(), loadReadings()]);
            if (isActive() && (currentFailures || emailFailures)) {
                const failures = [
                    currentFailures ? `${currentFailures} vessel sensor request(s) failed` : '',
                    emailFailures ? `${emailFailures} position request(s) failed` : '',
                ].filter(Boolean).join('; ');
                setLoadError(`${failures}. Last saved readings and position reports are retained. Delayed sensor reads retry automatically.`);
            }
        } catch {
            if (isActive()) setLoadError('Unable to load the configured vessel list. Please try Refresh.');
        } finally {
            if (isActive()) {
                setSetupLoading(false);
                setRefreshLoading(false);
                activeRequest.current = null;
                if (refreshQueued.current) {
                    refreshQueued.current = false;
                    fetchData();
                }
            }
        }
    }, []);

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, 1800000);
        const retryInterval = setInterval(async () => {
            if (retryRequest.current) return;
            const pending = latestData.current.filter(vessel => vessel.currentRetry);
            if (!pending.length) return;
            const controller = new AbortController();
            retryRequest.current = controller;
            try {
                await withWorkers(pending, async vessel => {
                    try {
                        const current = await GetMonitoringCurrent({ AccountId: vessel.KodeCabang, ObjectCode: vessel.ShipCode }, {
                            signal: controller.signal, timeout: 130000,
                        });
                        if (controller.signal.aborted) return;
                        if (!Array.isArray(current)) throw new Error('Invalid saved sensor response');
                        saveCurrentCache(vesselKey(vessel), current);
                        setData(previous => previous.map(existing => vesselKey(existing) === vesselKey(vessel) && existing.currentRetry
                            ? { ...existing, current: mergeCurrentRows(current, existing.current), currentState: current.length ? 'saved' : 'empty', currentRetry: false } : existing));
                    } catch (error) {
                        if (controller.signal.aborted) return;
                        setData(previous => previous.map(existing => vesselKey(existing) === vesselKey(vessel) && existing.currentRetry
                            ? { ...existing, ...readingFailure(existing, error) } : existing));
                    }
                });
            } finally { if (retryRequest.current === controller) retryRequest.current = null; }
        }, 30000);
        return () => {
            clearInterval(interval);
            clearInterval(retryInterval);
            retryRequest.current?.abort();
            retryRequest.current = null;
            activeRequest.current?.abort();
            activeRequest.current = null;
            refreshQueued.current = false;
        };
    }, [refresh]);

    const currentLoading = data.some(vessel => vessel.currentState === 'loading');
    const emailLoading = data.some(vessel => vessel.emailState === 'loading');
    return { data, setupLoading, currentLoading, refreshLoading, emailLoading, loadError, refresh };
}
